/**
 * subscriptions.jsx
 * ─────────────────────────────────────────────────────────────────────────────
 * Admin — Subscription Plans & User Subscriptions management
 * Route: /admin/subscriptions
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import supabase from "../supabase";
import {
  CreditCard, Plus, Edit2, Trash2, X, Check, ChevronLeft, ChevronRight,
  Search, RefreshCw, Users, Shield, LogOut, LayoutDashboard, Wallet,
  Receipt, Copy, UserCheck, HeadphonesIcon, Bell, CandlestickChart,
  BarChart2, ShoppingBag, Menu, AlertCircle, CheckCircle2, XCircle,
  TrendingUp, TrendingDown, MapPin, Zap, Star, Crown, Activity,
  BadgeCheck, DollarSign, Calendar, ArrowRight, MoreVertical,
  RotateCcw, Ban, Eye, Infinity, ChevronDown,
} from "lucide-react";

// ─── Design-system styles (mirrors admin_dashboard.jsx) ──────────────────────
const STYLES = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600&display=swap');
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
:root{
  --bg-base:#06060f;--bg-surface:#0a0a16;--bg-elevated:#0e0e1c;--bg-card:#111122;--bg-hover:#14142a;
  --border:rgba(255,255,255,0.055);--border-hi:rgba(255,255,255,0.11);--border-glow:rgba(99,102,241,0.3);
  --accent:#6366f1;--accent-light:#818cf8;--accent-dim:rgba(99,102,241,0.13);--accent-glow:rgba(99,102,241,0.22);
  --green:#34d399;--green-dim:rgba(52,211,153,0.13);
  --amber:#fbbf24;--amber-dim:rgba(251,191,36,0.13);
  --rose:#fb7185;--rose-dim:rgba(251,113,133,0.13);
  --cyan:#22d3ee;--cyan-dim:rgba(34,211,238,0.13);
  --purple:#a78bfa;--purple-dim:rgba(167,139,250,0.13);
  --orange:#fb923c;--orange-dim:rgba(251,146,60,0.13);
  --blue:#60a5fa;--blue-dim:rgba(96,165,250,0.13);
  --text-primary:#eeeeff;--text-secondary:#7878a0;--text-muted:#3e3e58;
  --font-sans:'Inter',system-ui,sans-serif;--font-mono:'JetBrains Mono',monospace;
  --sidebar-w:232px;--topbar-h:60px;
  --radius-sm:8px;--radius-md:14px;--radius-lg:18px;--radius-xl:22px;
}
html,body,#root{height:100%;background:var(--bg-base);font-family:var(--font-sans);color-scheme:dark;scroll-behavior:smooth;-webkit-text-size-adjust:100%}

/* Shell */
.ad-shell{display:flex;height:100vh;height:100dvh;overflow:hidden;background:var(--bg-base);position:relative}
.ad-shell::before{content:'';position:fixed;inset:0;pointer-events:none;z-index:0;background-image:url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='1'/%3E%3C/svg%3E");opacity:.018}

/* Overlay */
.ad-overlay{display:none;position:fixed;inset:0;z-index:40;background:rgba(0,0,0,.6);backdrop-filter:blur(2px);opacity:0;transition:opacity .28s ease;pointer-events:none}
.ad-overlay.open{display:block;opacity:1;pointer-events:auto}

/* Sidebar */
.ad-sidebar{width:var(--sidebar-w);flex-shrink:0;display:flex;flex-direction:column;height:100%;overflow:hidden;background:var(--bg-surface);border-right:1px solid var(--border);position:relative;z-index:50;transition:transform .3s cubic-bezier(.4,0,.2,1);will-change:transform}
.ad-sidebar::after{content:'';position:absolute;top:0;right:0;bottom:0;width:1px;background:linear-gradient(to bottom,transparent 0%,var(--accent-glow) 35%,rgba(99,102,241,.08) 70%,transparent 100%);pointer-events:none}
.ad-logo{display:flex;align-items:center;gap:11px;padding:0 18px;height:var(--topbar-h);border-bottom:1px solid var(--border);flex-shrink:0}
.ad-logo-close{display:none;margin-left:auto;width:28px;height:28px;border-radius:7px;background:none;border:none;cursor:pointer;align-items:center;justify-content:center;color:var(--text-muted);transition:all .15s;flex-shrink:0}
.ad-logo-close:hover{background:var(--rose-dim);color:var(--rose)}
.ad-logo-icon{width:32px;height:32px;border-radius:10px;background:linear-gradient(135deg,#6366f1 0%,#8b5cf6 50%,#a78bfa 100%);display:flex;align-items:center;justify-content:center;flex-shrink:0;box-shadow:0 0 0 1px rgba(99,102,241,.4),0 4px 16px rgba(99,102,241,.35),0 0 32px rgba(99,102,241,.15)}
.ad-logo-text{line-height:1}
.ad-logo-text strong{font-size:13.5px;font-weight:800;background:linear-gradient(to right,#eeeeff,#a5b4fc);-webkit-background-clip:text;-webkit-text-fill-color:transparent;letter-spacing:-.4px}
.ad-logo-text span{font-size:9.5px;color:var(--text-muted);display:block;margin-top:3px;letter-spacing:.8px;text-transform:uppercase;font-weight:500}
.ad-nav{flex:1;min-height:0;overflow-y:auto;padding:18px 10px;display:flex;flex-direction:column;gap:22px;-webkit-overflow-scrolling:touch;scroll-behavior:smooth}
.ad-nav::-webkit-scrollbar{width:3px}
.ad-nav::-webkit-scrollbar-track{background:transparent}
.ad-nav::-webkit-scrollbar-thumb{background:rgba(99,102,241,.18);border-radius:99px}
.ad-nav-section-label{font-size:9px;font-weight:700;letter-spacing:1.5px;text-transform:uppercase;color:var(--text-muted);padding:0 10px;margin-bottom:5px}
.ad-nav-items{display:flex;flex-direction:column;gap:1px}
.ad-nav-link{display:flex;align-items:center;gap:9px;padding:9px 10px;border-radius:var(--radius-sm);font-size:12.5px;font-weight:500;color:var(--text-secondary);text-decoration:none;transition:all .18s ease;position:relative;-webkit-tap-highlight-color:transparent;touch-action:manipulation}
.ad-nav-link:hover{background:var(--bg-hover);color:var(--text-primary)}
.ad-nav-link.active{background:linear-gradient(135deg,rgba(99,102,241,.18) 0%,rgba(139,92,246,.10) 100%);color:var(--accent-light);font-weight:600;box-shadow:inset 0 0 0 1px rgba(99,102,241,.15)}
.ad-nav-link.active::before{content:'';position:absolute;left:0;top:18%;bottom:18%;width:2.5px;border-radius:99px;background:linear-gradient(to bottom,var(--accent),var(--purple));box-shadow:0 0 10px var(--accent-glow)}
.ad-nav-link svg{flex-shrink:0;opacity:.65;transition:opacity .15s}
.ad-nav-link:hover svg,.ad-nav-link.active svg{opacity:1}
.ad-nav-chevron{margin-left:auto;opacity:.35}
.ad-user-card{flex-shrink:0;border-top:1px solid var(--border);padding:12px 10px;background:linear-gradient(to top,rgba(99,102,241,.04) 0%,transparent 100%)}
.ad-user-inner{display:flex;align-items:center;gap:10px;padding:9px 10px;border-radius:var(--radius-sm);transition:background .15s}
.ad-user-inner:hover{background:var(--bg-hover)}
.ad-user-avatar{width:32px;height:32px;border-radius:50%;background:linear-gradient(135deg,var(--accent-dim),rgba(139,92,246,.2));border:1.5px solid rgba(99,102,241,.35);display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:700;color:var(--accent-light);flex-shrink:0}
.ad-user-info{flex:1;min-width:0}
.ad-user-info strong{font-size:12px;font-weight:600;color:var(--text-primary);display:block;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.ad-user-info span{font-size:10px;color:var(--text-muted);display:block;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;margin-top:1px}
.ad-logout-btn{width:28px;height:28px;border-radius:7px;background:none;border:none;cursor:pointer;display:flex;align-items:center;justify-content:center;color:var(--text-muted);transition:all .15s;-webkit-tap-highlight-color:transparent}
.ad-logout-btn:hover{background:var(--rose-dim);color:var(--rose)}

/* Page */
.ad-page{flex:1;display:flex;flex-direction:column;overflow:hidden;position:relative;z-index:1;min-width:0}

/* Topbar */
.ad-topbar{flex-shrink:0;height:var(--topbar-h);background:rgba(10,10,22,.92);backdrop-filter:blur(14px);-webkit-backdrop-filter:blur(14px);border-bottom:1px solid var(--border);display:flex;align-items:center;justify-content:space-between;padding:0 24px;position:relative;gap:10px}
.ad-topbar::after{content:'';position:absolute;bottom:-1px;left:0;right:0;height:1px;background:linear-gradient(to right,transparent 0%,var(--accent-glow) 50%,transparent 100%)}
.ad-topbar-left{display:flex;align-items:center;gap:12px;min-width:0;flex:1}
.ad-topbar-title{font-size:15px;font-weight:700;color:var(--text-primary);letter-spacing:-.4px;white-space:nowrap}
.ad-topbar-sub{font-size:11px;color:var(--text-muted);margin-top:1px}
.ad-topbar-right{display:flex;align-items:center;gap:8px;flex-shrink:0}
.ad-hamburger{display:none;width:36px;height:36px;border-radius:var(--radius-sm);background:var(--bg-card);border:1px solid var(--border);cursor:pointer;align-items:center;justify-content:center;color:var(--text-secondary);transition:all .15s;flex-shrink:0;-webkit-tap-highlight-color:transparent}
.ad-hamburger:hover{background:var(--accent-dim);color:var(--accent-light);border-color:rgba(99,102,241,.25)}

/* Main scroll area */
.ad-main{flex:1;overflow-y:auto;padding:26px 28px;display:flex;flex-direction:column;gap:24px;-webkit-overflow-scrolling:touch;scroll-behavior:smooth;overscroll-behavior-y:contain}
.ad-main::-webkit-scrollbar{width:4px}
.ad-main::-webkit-scrollbar-track{background:transparent}
.ad-main::-webkit-scrollbar-thumb{background:rgba(99,102,241,.25);border-radius:99px}

/* ── Page header ── */
.sp-page-header{display:flex;align-items:flex-start;justify-content:space-between;gap:16px;flex-wrap:wrap}
.sp-page-header-text h1{font-size:22px;font-weight:800;color:var(--text-primary);letter-spacing:-.6px}
.sp-page-header-text p{font-size:12px;color:var(--text-muted);margin-top:4px}
.sp-page-header-actions{display:flex;align-items:center;gap:10px;flex-wrap:wrap}

/* ── Page tabs ── */
.sp-tabs{display:flex;align-items:center;gap:4px;background:var(--bg-card);border:1px solid var(--border);border-radius:var(--radius-md);padding:4px;width:fit-content}
.sp-tab{display:flex;align-items:center;gap:7px;padding:8px 16px;border-radius:calc(var(--radius-md) - 4px);font-size:12.5px;font-weight:600;color:var(--text-secondary);background:none;border:none;cursor:pointer;transition:all .18s;white-space:nowrap;-webkit-tap-highlight-color:transparent}
.sp-tab:hover{color:var(--text-primary);background:var(--bg-hover)}
.sp-tab.active{color:#fff;background:linear-gradient(135deg,var(--accent) 0%,#7c3aed 100%);box-shadow:0 2px 10px rgba(99,102,241,.35)}
.sp-tab-count{font-size:10px;font-weight:700;padding:1px 6px;border-radius:99px;background:rgba(255,255,255,.12)}
.sp-tab:not(.active) .sp-tab-count{background:var(--bg-elevated);color:var(--text-muted)}

/* ── KPI row ── */
.sp-kpi-row{display:grid;grid-template-columns:repeat(5,1fr);gap:14px}
.sp-kpi{background:var(--bg-card);border:1px solid var(--border);border-radius:var(--radius-md);padding:18px 20px;display:flex;align-items:center;gap:14px;transition:border-color .2s,box-shadow .2s,transform .18s;position:relative;overflow:hidden}
.sp-kpi:hover{border-color:var(--border-hi);box-shadow:0 4px 20px rgba(0,0,0,.3);transform:translateY(-1px)}
.sp-kpi::before{content:'';position:absolute;top:0;left:0;right:0;height:2px;background:var(--kpi-bar,transparent)}
.sp-kpi-icon{width:40px;height:40px;border-radius:12px;display:flex;align-items:center;justify-content:center;flex-shrink:0}
.sp-kpi-body{min-width:0;flex:1}
.sp-kpi-val{font-size:26px;font-weight:700;color:var(--text-primary);font-family:var(--font-mono);letter-spacing:-.9px;line-height:1}
.sp-kpi-label{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.9px;color:var(--text-muted);margin-top:4px}
.sp-kpi-sub{font-size:10.5px;font-weight:600;margin-top:5px;display:flex;align-items:center;gap:3px}
.sp-kpi-sub.up{color:var(--green)} .sp-kpi-sub.down{color:var(--rose)} .sp-kpi-sub.neu{color:var(--text-muted)}

/* ── Section label ── */
.sp-section-label{font-size:11px;font-weight:700;color:var(--text-secondary);letter-spacing:.6px;display:flex;align-items:center;gap:8px;text-transform:uppercase;margin-bottom:14px}
.sp-section-label::after{content:'';flex:1;height:1px;background:linear-gradient(to right,var(--border),transparent)}
.sp-section-count{font-size:10px;color:var(--text-muted);font-weight:500;text-transform:none;letter-spacing:0}

/* ── Plans grid ── */
.sp-plans-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(270px,1fr));gap:16px}

/* ── Plan card ── */
.sp-plan-card{background:var(--bg-card);border:1px solid var(--border);border-radius:var(--radius-lg);padding:22px;display:flex;flex-direction:column;gap:16px;position:relative;overflow:hidden;transition:border-color .22s,box-shadow .22s,transform .18s}
.sp-plan-card:hover{border-color:var(--border-hi);box-shadow:0 6px 32px rgba(0,0,0,.4);transform:translateY(-2px)}
.sp-plan-card.featured{border-color:rgba(99,102,241,.35);box-shadow:0 0 0 1px rgba(99,102,241,.1),0 8px 32px rgba(99,102,241,.1)}
.sp-plan-card::before{content:'';position:absolute;top:0;left:0;right:0;height:2px;background:var(--plan-bar,transparent)}
.sp-plan-card-glow{position:absolute;top:-40px;right:-40px;width:130px;height:130px;border-radius:50%;background:var(--plan-glow,transparent);filter:blur(32px);pointer-events:none;opacity:.35}

.sp-plan-top{display:flex;align-items:flex-start;justify-content:space-between;gap:12px}
.sp-plan-badge{display:inline-flex;align-items:center;gap:5px;padding:3px 10px;border-radius:99px;font-size:9.5px;font-weight:700;letter-spacing:.5px;text-transform:uppercase;width:fit-content;border:1px solid}
.sp-plan-icon-wrap{width:40px;height:40px;border-radius:12px;display:flex;align-items:center;justify-content:center;flex-shrink:0}
.sp-plan-name{font-size:19px;font-weight:800;color:var(--text-primary);letter-spacing:-.4px;margin-top:8px}
.sp-plan-price-row{display:flex;align-items:flex-end;gap:6px}
.sp-plan-price{font-size:34px;font-weight:800;color:var(--text-primary);font-family:var(--font-mono);letter-spacing:-1.5px;line-height:1}
.sp-plan-price-meta{font-size:11px;color:var(--text-muted);padding-bottom:4px;line-height:1.4}
.sp-plan-annual{font-size:11px;color:var(--green);font-weight:600;margin-top:3px}
.sp-plan-save{color:var(--text-muted);font-weight:400;margin-left:5px}

.sp-plan-limits{display:flex;flex-wrap:wrap;gap:5px 12px}
.sp-plan-limit{font-size:10.5px;color:var(--text-muted);font-family:var(--font-mono);display:flex;align-items:center;gap:4px}
.sp-plan-limit svg{opacity:.5;flex-shrink:0}

.sp-plan-divider{height:1px;background:var(--border)}

.sp-plan-features{display:flex;flex-direction:column;gap:7px;flex:1}
.sp-plan-feature{display:flex;align-items:flex-start;gap:7px;font-size:12px;color:var(--text-secondary);line-height:1.4}
.sp-plan-feature .check{flex-shrink:0;margin-top:1px}
.sp-plan-feature.muted{color:var(--text-muted)}
.sp-plan-more{font-size:11px;color:var(--text-muted);margin-top:2px;font-style:italic}

.sp-plan-footer{display:flex;align-items:center;justify-content:space-between;margin-top:auto;padding-top:4px}
.sp-plan-subs{font-size:11px;color:var(--text-muted);display:flex;align-items:center;gap:5px}
.sp-plan-subs strong{color:var(--text-secondary);font-family:var(--font-mono)}
.sp-plan-btns{display:flex;gap:6px}

/* ── Buttons ── */
.sp-btn{display:inline-flex;align-items:center;gap:7px;padding:8px 16px;border-radius:var(--radius-sm);font-size:12px;font-weight:600;cursor:pointer;transition:all .18s;border:none;font-family:var(--font-sans);white-space:nowrap;-webkit-tap-highlight-color:transparent;text-decoration:none}
.sp-btn-primary{background:linear-gradient(135deg,var(--accent) 0%,#7c3aed 100%);color:#fff;box-shadow:0 2px 12px rgba(99,102,241,.35)}
.sp-btn-primary:hover{box-shadow:0 4px 20px rgba(99,102,241,.5);transform:translateY(-1px)}
.sp-btn-ghost{background:var(--bg-elevated);border:1px solid var(--border-hi);color:var(--text-secondary)}
.sp-btn-ghost:hover{background:var(--bg-hover);color:var(--text-primary);border-color:var(--border-glow)}
.sp-btn-danger{background:var(--rose-dim);border:1px solid rgba(251,113,133,.22);color:var(--rose)}
.sp-btn-danger:hover{background:rgba(251,113,133,.2);box-shadow:0 0 12px rgba(251,113,133,.2)}
.sp-btn-success{background:var(--green-dim);border:1px solid rgba(52,211,153,.22);color:var(--green)}
.sp-btn-success:hover{background:rgba(52,211,153,.2)}
.sp-btn-sm{padding:5px 11px;font-size:11px}
.sp-btn-xs{padding:3px 8px;font-size:10.5px;border-radius:6px}
.sp-btn-icon{width:30px;height:30px;padding:0;justify-content:center;border-radius:7px}
.sp-btn-icon-sm{width:26px;height:26px;padding:0;justify-content:center;border-radius:6px}
.sp-btn:disabled{opacity:.4;cursor:not-allowed;transform:none!important}

/* ── Panel ── */
.sp-panel{background:var(--bg-card);border:1px solid var(--border);border-radius:var(--radius-md);overflow:hidden;display:flex;flex-direction:column;transition:border-color .2s}
.sp-panel:hover{border-color:var(--border-hi)}
.sp-panel-header{padding:16px 20px;border-bottom:1px solid var(--border);display:flex;align-items:center;justify-content:space-between;gap:12px;flex-wrap:wrap;background:linear-gradient(to bottom,rgba(255,255,255,.02),transparent);flex-shrink:0}
.sp-panel-title{font-size:13px;font-weight:700;color:var(--text-primary);letter-spacing:-.2px}
.sp-panel-sub{font-size:11px;color:var(--text-muted);margin-top:2px}

/* ── Toolbar ── */
.sp-toolbar{display:flex;align-items:center;gap:8px;flex-wrap:wrap}
.sp-search-wrap{position:relative}
.sp-search-wrap svg{position:absolute;left:11px;top:50%;transform:translateY(-50%);color:var(--text-muted);pointer-events:none}
.sp-search{background:var(--bg-elevated);border:1px solid var(--border-hi);border-radius:var(--radius-sm);padding:7px 12px 7px 33px;font-size:12px;color:var(--text-primary);font-family:var(--font-sans);outline:none;width:220px;transition:border-color .18s,box-shadow .18s}
.sp-search::placeholder{color:var(--text-muted)}
.sp-search:focus{border-color:rgba(99,102,241,.4);box-shadow:0 0 0 3px rgba(99,102,241,.08)}
.sp-select{background:var(--bg-elevated);border:1px solid var(--border-hi);border-radius:var(--radius-sm);padding:7px 30px 7px 11px;font-size:12px;color:var(--text-primary);font-family:var(--font-sans);outline:none;cursor:pointer;appearance:none;-webkit-appearance:none;background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%237878a0' stroke-width='2.5'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E");background-repeat:no-repeat;background-position:right 9px center;transition:border-color .18s;min-width:130px}
.sp-select:focus{border-color:rgba(99,102,241,.4);box-shadow:0 0 0 3px rgba(99,102,241,.08)}

/* ── Table ── */
.sp-table-wrap{overflow-x:auto;-webkit-overflow-scrolling:touch;flex:1}
table.sp-table{width:100%;border-collapse:collapse;font-size:12.5px}
table.sp-table thead tr{border-bottom:1px solid var(--border)}
table.sp-table th{padding:10px 16px;text-align:left;font-size:9px;font-weight:700;letter-spacing:1.2px;text-transform:uppercase;color:var(--text-muted);white-space:nowrap;cursor:pointer;user-select:none;transition:color .15s}
table.sp-table th:hover{color:var(--text-secondary)}
table.sp-table tbody tr{border-bottom:1px solid rgba(255,255,255,.035);transition:background .12s;cursor:pointer}
table.sp-table tbody tr:last-child{border-bottom:none}
table.sp-table tbody tr:hover{background:rgba(99,102,241,.04)}
table.sp-table td{padding:12px 16px;vertical-align:middle}

/* ── Cells ── */
.sp-avatar-cell{display:flex;align-items:center;gap:10px}
.sp-avatar{width:30px;height:30px;border-radius:50%;background:linear-gradient(135deg,var(--accent-dim),rgba(139,92,246,.18));border:1.5px solid rgba(99,102,241,.2);display:flex;align-items:center;justify-content:center;font-size:10px;font-weight:700;color:var(--accent-light);flex-shrink:0}
.sp-cell-name{font-size:12.5px;font-weight:500;color:var(--text-primary)}
.sp-cell-email{font-size:10.5px;color:var(--text-muted);margin-top:1px}
.sp-cell-mono{font-size:11px;color:var(--text-secondary);font-family:var(--font-mono);letter-spacing:-.2px}
.sp-cell-date{font-size:11px;color:var(--text-muted);font-family:var(--font-mono)}

/* ── Chips ── */
.sp-chip{display:inline-flex;align-items:center;gap:5px;padding:3px 9px;border-radius:99px;font-size:10px;font-weight:700;letter-spacing:.4px;text-transform:capitalize;border:1px solid}
.sp-chip.active{background:var(--green-dim);color:var(--green);border-color:rgba(52,211,153,.2)}
.sp-chip.cancelled{background:var(--rose-dim);color:var(--rose);border-color:rgba(251,113,133,.2)}
.sp-chip.expired{background:var(--amber-dim);color:var(--amber);border-color:rgba(251,191,36,.2)}
.sp-chip.paused{background:var(--purple-dim);color:var(--purple);border-color:rgba(167,139,250,.2)}
.sp-chip.monthly{background:var(--blue-dim);color:var(--blue);border-color:rgba(96,165,250,.2)}
.sp-chip.annual{background:var(--cyan-dim);color:var(--cyan);border-color:rgba(34,211,238,.2)}

/* ── Plan pill ── */
.sp-plan-pill{display:inline-flex;align-items:center;gap:5px;padding:3px 10px;border-radius:99px;font-size:10px;font-weight:700;letter-spacing:.3px;background:var(--accent-dim);color:var(--accent-light);border:1px solid rgba(99,102,241,.18)}

/* ── Pulse dot ── */
.sp-dot-pulse{width:5px;height:5px;border-radius:50%;background:var(--green);box-shadow:0 0 5px var(--green);animation:sp-pulse 2s infinite;display:inline-block;flex-shrink:0}
@keyframes sp-pulse{0%,100%{opacity:1;transform:scale(1)}50%{opacity:.5;transform:scale(.7)}}

/* ── Pagination ── */
.sp-pagination{display:flex;align-items:center;justify-content:space-between;padding:14px 20px;border-top:1px solid var(--border);flex-shrink:0;flex-wrap:wrap;gap:10px}
.sp-pagination-info{font-size:11px;color:var(--text-muted)}
.sp-pagination-btns{display:flex;align-items:center;gap:4px}
.sp-page-btn{width:30px;height:30px;border-radius:7px;background:none;border:1px solid var(--border);cursor:pointer;display:flex;align-items:center;justify-content:center;color:var(--text-muted);font-size:11.5px;font-family:var(--font-sans);transition:all .15s;-webkit-tap-highlight-color:transparent}
.sp-page-btn:hover{background:var(--bg-hover);color:var(--text-primary);border-color:var(--border-hi)}
.sp-page-btn.active{background:var(--accent-dim);color:var(--accent-light);border-color:rgba(99,102,241,.3);font-weight:700}
.sp-page-btn:disabled{opacity:.35;cursor:not-allowed}

/* ── Empty ── */
.sp-empty{display:flex;flex-direction:column;align-items:center;justify-content:center;padding:52px;color:var(--text-muted);gap:10px;font-size:12px;text-align:center}
.sp-empty svg{opacity:.35;margin-bottom:4px}

/* ── Skeleton ── */
.sp-skel{height:10px;border-radius:5px;background:linear-gradient(90deg,var(--bg-hover) 25%,rgba(99,102,241,.05) 50%,var(--bg-hover) 75%);background-size:200% 100%;animation:sp-shimmer 1.8s infinite}
@keyframes sp-shimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}

/* ── Toast ── */
.sp-toast-wrap{position:fixed;bottom:24px;right:24px;z-index:9999;display:flex;flex-direction:column;gap:8px;pointer-events:none}
.sp-toast{display:flex;align-items:center;gap:10px;padding:12px 18px;border-radius:var(--radius-md);font-size:12.5px;font-weight:500;color:var(--text-primary);backdrop-filter:blur(12px);border:1px solid;animation:sp-toast-in .3s ease;pointer-events:auto;min-width:240px;max-width:360px;box-shadow:0 8px 32px rgba(0,0,0,.4)}
.sp-toast.success{background:rgba(14,14,28,.96);border-color:rgba(52,211,153,.25)}
.sp-toast.error{background:rgba(14,14,28,.96);border-color:rgba(251,113,133,.25)}
.sp-toast.info{background:rgba(14,14,28,.96);border-color:rgba(99,102,241,.25)}
@keyframes sp-toast-in{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}

/* ── Modal ── */
.sp-modal-bg{position:fixed;inset:0;z-index:200;background:rgba(0,0,0,.65);backdrop-filter:blur(4px);display:flex;align-items:center;justify-content:center;padding:20px;animation:sp-fade .22s ease}
@keyframes sp-fade{from{opacity:0}to{opacity:1}}
.sp-modal{background:var(--bg-surface);border:1px solid var(--border-hi);border-radius:var(--radius-xl);width:100%;max-width:580px;max-height:90vh;overflow-y:auto;-webkit-overflow-scrolling:touch;animation:sp-modal-up .24s cubic-bezier(.34,1.56,.64,1)}
@keyframes sp-modal-up{from{transform:translateY(16px) scale(.98);opacity:0}to{transform:translateY(0) scale(1);opacity:1}}
.sp-modal-header{display:flex;align-items:center;justify-content:space-between;padding:20px 24px;border-bottom:1px solid var(--border);position:sticky;top:0;background:var(--bg-surface);z-index:1}
.sp-modal-title{font-size:15px;font-weight:700;color:var(--text-primary);letter-spacing:-.3px}
.sp-modal-body{padding:24px;display:flex;flex-direction:column;gap:18px}
.sp-modal-footer{padding:16px 24px;border-top:1px solid var(--border);display:flex;justify-content:flex-end;gap:10px;position:sticky;bottom:0;background:var(--bg-surface);z-index:1}

/* ── Form ── */
.sp-field{display:flex;flex-direction:column;gap:6px}
.sp-label{font-size:11px;font-weight:600;color:var(--text-secondary);letter-spacing:.3px;text-transform:uppercase}
.sp-input{background:var(--bg-elevated);border:1px solid var(--border-hi);border-radius:var(--radius-sm);padding:9px 13px;font-size:13px;color:var(--text-primary);font-family:var(--font-sans);outline:none;transition:border-color .18s,box-shadow .18s;width:100%}
.sp-input::placeholder{color:var(--text-muted)}
.sp-input:focus{border-color:rgba(99,102,241,.45);box-shadow:0 0 0 3px rgba(99,102,241,.09)}
.sp-input-hint{font-size:10.5px;color:var(--text-muted)}
.sp-field-row{display:grid;grid-template-columns:1fr 1fr;gap:14px}
.sp-field-row-3{display:grid;grid-template-columns:1fr 1fr 1fr;gap:14px}

/* ── Confirm ── */
.sp-confirm-bg{position:fixed;inset:0;z-index:300;background:rgba(0,0,0,.72);backdrop-filter:blur(4px);display:flex;align-items:center;justify-content:center;padding:20px}
.sp-confirm{background:var(--bg-surface);border:1px solid var(--border-hi);border-radius:var(--radius-lg);padding:28px;max-width:380px;width:100%;animation:sp-modal-up .22s cubic-bezier(.34,1.56,.64,1)}
.sp-confirm-icon{width:44px;height:44px;border-radius:14px;display:flex;align-items:center;justify-content:center;margin-bottom:16px}
.sp-confirm h3{font-size:15px;font-weight:700;color:var(--text-primary);margin-bottom:8px}
.sp-confirm p{font-size:12.5px;color:var(--text-muted);line-height:1.55}
.sp-confirm-actions{display:flex;gap:10px;margin-top:22px;justify-content:flex-end}

/* ── Drawer (subscriber detail) ── */
.sp-drawer-bg{position:fixed;inset:0;z-index:150;background:rgba(0,0,0,.55);backdrop-filter:blur(3px);animation:sp-fade .22s ease}
.sp-drawer{position:fixed;top:0;right:0;bottom:0;width:100%;max-width:420px;background:var(--bg-surface);border-left:1px solid var(--border-hi);display:flex;flex-direction:column;z-index:151;animation:sp-drawer-in .28s cubic-bezier(.4,0,.2,1);overflow:hidden}
@keyframes sp-drawer-in{from{transform:translateX(100%)}to{transform:translateX(0)}}
.sp-drawer-header{display:flex;align-items:center;justify-content:space-between;padding:18px 20px;border-bottom:1px solid var(--border);flex-shrink:0}
.sp-drawer-title{font-size:14px;font-weight:700;color:var(--text-primary)}
.sp-drawer-body{flex:1;overflow-y:auto;-webkit-overflow-scrolling:touch;padding:20px;display:flex;flex-direction:column;gap:20px}
.sp-drawer-body::-webkit-scrollbar{width:3px}
.sp-drawer-body::-webkit-scrollbar-thumb{background:rgba(99,102,241,.18);border-radius:99px}
.sp-drawer-section{display:flex;flex-direction:column;gap:10px}
.sp-drawer-section-title{font-size:9.5px;font-weight:700;text-transform:uppercase;letter-spacing:1.2px;color:var(--text-muted)}
.sp-drawer-row{display:flex;align-items:center;justify-content:space-between;padding:10px 14px;background:var(--bg-card);border-radius:var(--radius-sm);border:1px solid var(--border)}
.sp-drawer-row-label{font-size:11.5px;color:var(--text-muted)}
.sp-drawer-row-val{font-size:12px;font-weight:600;color:var(--text-primary);text-align:right}
.sp-drawer-actions{display:flex;flex-direction:column;gap:8px;padding:0 20px 20px;flex-shrink:0}

/* ── Responsive ── */
@media(min-width:1400px){:root{--sidebar-w:252px}.ad-main{padding:30px 36px}.ad-topbar{padding:0 36px}}
@media(max-width:1280px){.sp-kpi-row{grid-template-columns:repeat(3,1fr)}}
@media(max-width:1023px){:root{--sidebar-w:220px}.ad-main{padding:20px;gap:20px}.ad-topbar{padding:0 16px}}
@media(max-width:899px){.sp-kpi-row{grid-template-columns:repeat(2,1fr)}}
@media(max-width:767px){
  :root{--topbar-h:56px}
  .ad-hamburger{display:flex}
  .ad-logo-close{display:flex}
  .ad-sidebar{position:fixed;top:0;left:0;bottom:0;height:100%;height:100dvh;overflow:hidden;transform:translateX(-100%);box-shadow:4px 0 32px rgba(0,0,0,.6)}
  .ad-sidebar.open{transform:translateX(0)}
  .ad-topbar{padding:0 14px}
  .ad-topbar-sub{display:none}
  .ad-main{padding:14px;gap:16px}
  .sp-kpi-row{grid-template-columns:repeat(2,1fr);gap:10px}
  .sp-plans-grid{grid-template-columns:1fr}
  .sp-plan-card{padding:18px}
  .sp-panel-header{flex-direction:column;align-items:stretch}
  .sp-toolbar{flex-direction:column;align-items:stretch}
  .sp-search{width:100%}
  .sp-select{width:100%}
  .sp-field-row,.sp-field-row-3{grid-template-columns:1fr}
  .sp-page-header{flex-direction:column;gap:12px}
  .sp-page-header-actions{width:100%;justify-content:flex-end}
  .sp-tabs{width:100%}
  .sp-tab{flex:1;justify-content:center}
  .sp-drawer{max-width:100%}
  .sp-toast-wrap{left:14px;right:14px;bottom:14px}
}
@media(max-width:479px){
  .sp-kpi-row{grid-template-columns:1fr 1fr;gap:8px}
  .sp-kpi{padding:14px 12px;gap:10px}
  .sp-kpi-val{font-size:22px}
}
`;

function StyleInjector() {
  useEffect(() => {
    const el = document.createElement("style");
    el.textContent = STYLES;
    document.head.appendChild(el);
    return () => document.head.removeChild(el);
  }, []);
  return null;
}

// ─── Nav config ───────────────────────────────────────────────────────────────
const NAV_SECTIONS = [
  { label: "Overview",        items: [{ to: "/admin",               label: "Dashboard",      icon: LayoutDashboard, end: true }] },
  { label: "Users & Finance", items: [
    { to: "/admin/users",        label: "Users",        icon: Users        },
    { to: "/admin/wallets",      label: "Wallets",      icon: Wallet       },
    { to: "/admin/transactions", label: "Transactions", icon: Receipt      },
  ]},
  { label: "Trading", items: [
    { to: "/admin/copy-trades",  label: "Copy trades",   icon: Copy             },
    { to: "/admin/traders",      label: "Hired traders", icon: UserCheck        },
    { to: "/admin/trading",      label: "Open trades",   icon: CandlestickChart },
    { to: "/admin/stock-trades", label: "Stock trades",  icon: BarChart2        },
    { to: "/admin/marketplace",  label: "Marketplace",   icon: ShoppingBag      },
  ]},
  { label: "Communication", items: [
    { to: "/admin/support",       label: "Support",       icon: HeadphonesIcon },
    { to: "/admin/notifications", label: "Notifications", icon: Bell           },
  ]},
  { label: "Settings", items: [
    { to: "/admin/subscriptions",     label: "Subscriptions",    icon: CreditCard },
    { to: "/admin/payment-addresses", label: "Payment Addresses", icon: MapPin    },
    { to: "/admin/market-subscriptions",  label: "Market Subscriptions",  icon: MapPin     },
  ]},
];

function SidebarLink({ to, label, icon: Icon, end, onClick }) {
  return (
    <NavLink to={to} end={end} onClick={onClick}
      className={({ isActive }) => `ad-nav-link${isActive ? " active" : ""}`}>
      {({ isActive }) => (
        <>
          <Icon size={14} />
          <span style={{ flex: 1 }}>{label}</span>
          {isActive && <ChevronRight size={10} className="ad-nav-chevron" />}
        </>
      )}
    </NavLink>
  );
}

// ─── Utilities ────────────────────────────────────────────────────────────────
const fmt$ = (v) => v == null ? "—"
  : new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(v);

const fmtDate = (s) => !s ? "—"
  : new Date(s).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });

const fmtDateShort = (s) => !s ? "—"
  : new Date(s).toLocaleDateString("en-GB", { day: "2-digit", month: "short" });

const initials = (a, b) => `${a?.[0] ?? ""}${b?.[0] ?? ""}`.toUpperCase() || "?";

const daysLeft = (end) => {
  if (!end) return null;
  const diff = Math.ceil((new Date(end) - new Date()) / 86400000);
  return diff;
};

// ─── Plan colour theme ────────────────────────────────────────────────────────
const PLAN_THEMES = {
  free:       { bar: "linear-gradient(to right,#2d2d4a,#1e1e36)",   glow: "transparent",    badge: { bg: "rgba(99,102,241,.1)",  color: "#818cf8", border: "rgba(99,102,241,.2)"  }, icon: Zap,    iconBg: "rgba(99,102,241,.12)",  iconColor: "#818cf8" },
  starter:    { bar: "linear-gradient(to right,#34d399,#059669)",   glow: "rgba(52,211,153,.4)",  badge: { bg: "rgba(52,211,153,.1)",  color: "#34d399", border: "rgba(52,211,153,.25)" }, icon: Zap,    iconBg: "rgba(52,211,153,.12)",  iconColor: "#34d399" },
  basic:      { bar: "linear-gradient(to right,#34d399,#0d9488)",   glow: "rgba(52,211,153,.4)",  badge: { bg: "rgba(52,211,153,.1)",  color: "#34d399", border: "rgba(52,211,153,.25)" }, icon: Zap,    iconBg: "rgba(52,211,153,.12)",  iconColor: "#34d399" },
  pro:        { bar: "linear-gradient(to right,#6366f1,#8b5cf6)",   glow: "rgba(99,102,241,.5)",  badge: { bg: "rgba(99,102,241,.15)", color: "#a5b4fc", border: "rgba(99,102,241,.3)"  }, icon: Star,   iconBg: "rgba(99,102,241,.15)",  iconColor: "#818cf8", featured: true },
  premium:    { bar: "linear-gradient(to right,#f59e0b,#ef4444)",   glow: "rgba(251,191,36,.45)", badge: { bg: "rgba(251,191,36,.1)",  color: "#fbbf24", border: "rgba(251,191,36,.25)" }, icon: Crown,  iconBg: "rgba(251,191,36,.12)",  iconColor: "#fbbf24" },
  enterprise: { bar: "linear-gradient(to right,#a78bfa,#ec4899)",   glow: "rgba(167,139,250,.5)", badge: { bg: "rgba(167,139,250,.12)", color: "#c4b5fd", border: "rgba(167,139,250,.3)"}, icon: Crown,  iconBg: "rgba(167,139,250,.15)", iconColor: "#a78bfa" },
};
const getPlanTheme = (name) => PLAN_THEMES[name?.toLowerCase()] ?? PLAN_THEMES.basic;

const PAGE_SIZE = 12;

// ─── Toast ────────────────────────────────────────────────────────────────────
function Toasts({ list }) {
  return (
    <div className="sp-toast-wrap">
      {list.map((t) => (
        <div key={t.id} className={`sp-toast ${t.type}`}>
          {t.type === "success" && <CheckCircle2 size={14} style={{ color: "var(--green)", flexShrink: 0 }} />}
          {t.type === "error"   && <XCircle      size={14} style={{ color: "var(--rose)",  flexShrink: 0 }} />}
          {t.type === "info"    && <Activity      size={14} style={{ color: "var(--accent-light)", flexShrink: 0 }} />}
          <span style={{ flex: 1 }}>{t.msg}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Plan card ────────────────────────────────────────────────────────────────
function PlanCard({ plan, subCount, onEdit, onDelete }) {
  const theme    = getPlanTheme(plan.name);
  const PlanIcon = theme.icon;

  // features is a jsonb object: { desc, tier, items: [{ yes, label }], accent, featured }
  const f        = plan.features && typeof plan.features === "object" && !Array.isArray(plan.features)
    ? plan.features
    : {};
  const desc     = f.desc ?? "";
  const tierName = f.tier ?? "";
  const items    = Array.isArray(f.items) ? f.items : [];
  const featured = !!f.featured || !!theme.featured;

  const savePct = plan.monthly_price > 0 && plan.annual_price > 0
    ? Math.round(100 - (plan.annual_price / (plan.monthly_price * 12)) * 100)
    : 0;

  return (
    <div
      className={`sp-plan-card${featured ? " featured" : ""}`}
      style={{ "--plan-bar": theme.bar, "--plan-glow": theme.glow }}
    >
      <div className="sp-plan-card-glow" />

      {/* Top */}
      <div className="sp-plan-top">
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="sp-plan-badge"
            style={{ background: theme.badge.bg, color: theme.badge.color, borderColor: theme.badge.border }}>
            <PlanIcon size={9} /> {tierName || plan.name}
          </div>
          <div className="sp-plan-name">{plan.name}</div>
          {desc && <div style={{ fontSize: 11.5, color: "var(--text-muted)", marginTop: 4, lineHeight: 1.4 }}>{desc}</div>}
        </div>
        <div className="sp-plan-icon-wrap" style={{ background: theme.iconBg }}>
          <PlanIcon size={18} style={{ color: theme.iconColor }} />
        </div>
      </div>

      {/* Pricing */}
      <div>
        <div className="sp-plan-price-row">
          <div className="sp-plan-price">{fmt$(plan.monthly_price)}</div>
          <div className="sp-plan-price-meta">/ month<br /><span style={{ fontSize: 10 }}>billed monthly</span></div>
        </div>
        {plan.annual_price > 0 && (
          <div className="sp-plan-annual">
            {fmt$(plan.annual_price)} / year
            {savePct > 0 && <span className="sp-plan-save">— save {savePct}%</span>}
          </div>
        )}
      </div>

      {/* Limits */}
      <div className="sp-plan-limits">
        <div className="sp-plan-limit"><Users size={10} />{plan.max_copy_traders} copy traders</div>
        <div className="sp-plan-limit"><Bell  size={10} />{plan.max_alerts} alerts</div>
        <div className="sp-plan-limit"><Eye   size={10} />{plan.max_watchlist} watchlist</div>
        <div className="sp-plan-limit">
          {plan.max_allocation
            ? <><DollarSign size={10} />{fmt$(plan.max_allocation)} max</>
            : <><Infinity size={10} />Unlimited alloc.</>}
        </div>
        {plan.api_calls_per_day > 0 && (
          <div className="sp-plan-limit"><Zap size={10} />{plan.api_calls_per_day.toLocaleString()} API/day</div>
        )}
      </div>

      <div className="sp-plan-divider" />

      {/* Features */}
      <div className="sp-plan-features">
        {items.length === 0 && (
          <div className="sp-plan-feature muted"><AlertCircle size={11} className="check" />No features defined</div>
        )}
        {items.slice(0, 6).map((it, i) => (
          <div key={i} className={`sp-plan-feature${it.yes ? "" : " muted"}`}>
            {it.yes
              ? <Check size={11} className="check" style={{ color: theme.iconColor }} />
              : <XCircle size={11} className="check" style={{ color: "var(--text-muted)" }} />}
            <span>{it.label}</span>
          </div>
        ))}
        {items.length > 6 && <div className="sp-plan-more">+{items.length - 6} more features</div>}
      </div>

      {/* Footer */}
      <div className="sp-plan-footer">
        <div className="sp-plan-subs">
          <Users size={11} />
          <strong>{subCount ?? 0}</strong>
          <span>subscriber{(subCount ?? 0) !== 1 ? "s" : ""}</span>
        </div>
        <div className="sp-plan-btns">
          <button className="sp-btn sp-btn-ghost sp-btn-icon sp-btn-sm" title="Edit plan" onClick={() => onEdit(plan)}>
            <Edit2 size={12} />
          </button>
          <button className="sp-btn sp-btn-danger sp-btn-icon sp-btn-sm" title="Delete plan" onClick={() => onDelete(plan)}>
            <Trash2 size={12} />
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Plan modal (create / edit) ───────────────────────────────────────────────
// features jsonb shape: { desc, tier, items: [{ yes, label }], accent, featured }
const BLANK = {
  name: "", monthly_price: "", annual_price: "",
  max_copy_traders: 3, max_allocation: "", max_alerts: 10,
  max_watchlist: 20, api_calls_per_day: 0,
  desc: "", tier: "", items: [], accent: false, featured: false,
};

function PlanModal({ plan, onClose, onSaved, addToast }) {
  const isEdit = !!plan?.id;
  const [form, setForm] = useState(() => {
    if (!isEdit) return BLANK;
    const f = plan.features && typeof plan.features === "object" && !Array.isArray(plan.features)
      ? plan.features
      : {};
    return {
      ...plan,
      max_allocation: plan.max_allocation ?? "",
      desc:     f.desc ?? "",
      tier:     f.tier ?? "",
      items:    Array.isArray(f.items) ? f.items.map((it) => ({ yes: !!it.yes, label: it.label ?? "" })) : [],
      accent:   !!f.accent,
      featured: !!f.featured,
    };
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const addItem    = () => setForm((p) => ({ ...p, items: [...p.items, { yes: true, label: "" }] }));
  const removeItem = (i) => setForm((p) => ({ ...p, items: p.items.filter((_, idx) => idx !== i) }));
  const updateItem = (i, patch) => setForm((p) => ({
    ...p, items: p.items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)),
  }));

  async function save() {
    if (!form.name.trim()) { addToast("Plan name is required", "error"); return; }
    setSaving(true);
    const payload = {
      name:              form.name.trim(),
      monthly_price:     parseFloat(form.monthly_price) || 0,
      annual_price:      parseFloat(form.annual_price)  || 0,
      max_copy_traders:  parseInt(form.max_copy_traders) || 0,
      max_allocation:    form.max_allocation !== "" ? parseFloat(form.max_allocation) : null,
      max_alerts:        parseInt(form.max_alerts)    || 0,
      max_watchlist:     parseInt(form.max_watchlist)  || 0,
      api_calls_per_day: parseInt(form.api_calls_per_day) || 0,
      features: {
        desc:     form.desc.trim(),
        tier:     form.tier.trim(),
        items:    form.items.map((it) => ({ yes: !!it.yes, label: it.label.trim() })).filter((it) => it.label),
        accent:   !!form.accent,
        featured: !!form.featured,
      },
    };
    const { data: savedRows, error } = isEdit
      ? await supabase.from("subscription_plans").update(payload).eq("id", plan.id).select()
      : await supabase.from("subscription_plans").insert(payload).select();
    setSaving(false);
    if (error) { addToast(error.message, "error"); return; }
    if (isEdit && (!savedRows || savedRows.length === 0)) {
      addToast("Update blocked — no rows changed (check RLS policy on subscription_plans)", "error");
      return;
    }
    addToast(isEdit ? "Plan updated successfully" : "Plan created successfully", "success");
    onSaved();
  }

  return (
    <div className="sp-modal-bg" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="sp-modal">
        <div className="sp-modal-header">
          <span className="sp-modal-title">{isEdit ? `Edit — ${plan.name}` : "Create subscription plan"}</span>
          <button className="sp-btn sp-btn-ghost sp-btn-icon" onClick={onClose}><X size={14} /></button>
        </div>
        <div className="sp-modal-body">

          <div className="sp-field">
            <label className="sp-label">Plan name *</label>
            <input className="sp-input" placeholder="e.g. Pro, Enterprise…" value={form.name} onChange={(e) => set("name", e.target.value)} />
          </div>

          <div className="sp-field-row">
            <div className="sp-field">
              <label className="sp-label">Monthly price ($)</label>
              <input className="sp-input" type="number" min="0" step="0.01" placeholder="0.00"
                value={form.monthly_price} onChange={(e) => set("monthly_price", e.target.value)} />
            </div>
            <div className="sp-field">
              <label className="sp-label">Annual price ($)</label>
              <input className="sp-input" type="number" min="0" step="0.01" placeholder="0.00"
                value={form.annual_price} onChange={(e) => set("annual_price", e.target.value)} />
              {form.monthly_price > 0 && form.annual_price > 0 && (
                <span className="sp-input-hint" style={{ color: "var(--green)" }}>
                  Saves {Math.round(100 - (parseFloat(form.annual_price) / (parseFloat(form.monthly_price) * 12)) * 100)}% vs monthly
                </span>
              )}
            </div>
          </div>

          <div className="sp-field-row-3">
            <div className="sp-field">
              <label className="sp-label">Copy traders</label>
              <input className="sp-input" type="number" min="0" value={form.max_copy_traders} onChange={(e) => set("max_copy_traders", e.target.value)} />
            </div>
            <div className="sp-field">
              <label className="sp-label">Max alerts</label>
              <input className="sp-input" type="number" min="0" value={form.max_alerts} onChange={(e) => set("max_alerts", e.target.value)} />
            </div>
            <div className="sp-field">
              <label className="sp-label">Watchlist slots</label>
              <input className="sp-input" type="number" min="0" value={form.max_watchlist} onChange={(e) => set("max_watchlist", e.target.value)} />
            </div>
          </div>

          <div className="sp-field-row">
            <div className="sp-field">
              <label className="sp-label">Max allocation ($)</label>
              <input className="sp-input" type="number" min="0" placeholder="Leave blank = unlimited"
                value={form.max_allocation} onChange={(e) => set("max_allocation", e.target.value)} />
              <span className="sp-input-hint">Leave blank for unlimited</span>
            </div>
            <div className="sp-field">
              <label className="sp-label">API calls / day</label>
              <input className="sp-input" type="number" min="0"
                value={form.api_calls_per_day} onChange={(e) => set("api_calls_per_day", e.target.value)} />
              <span className="sp-input-hint">0 = no API access</span>
            </div>
          </div>

          <div className="sp-field">
            <label className="sp-label">Short description</label>
            <input className="sp-input" placeholder="e.g. For active traders who want full control and advanced tools."
              value={form.desc} onChange={(e) => set("desc", e.target.value)} />
          </div>

          <div className="sp-field-row">
            <div className="sp-field">
              <label className="sp-label">Tier badge label</label>
              <input className="sp-input" placeholder="e.g. Most Popular, Business, Enterprise…"
                value={form.tier} onChange={(e) => set("tier", e.target.value)} />
            </div>
            <div className="sp-field" style={{ flexDirection: "row", alignItems: "center", gap: 18, paddingTop: 18 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--text-secondary)", cursor: "pointer" }}>
                <input type="checkbox" checked={form.accent} onChange={(e) => set("accent", e.target.checked)} />
                Accent styling
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--text-secondary)", cursor: "pointer" }}>
                <input type="checkbox" checked={form.featured} onChange={(e) => set("featured", e.target.checked)} />
                Featured plan
              </label>
            </div>
          </div>

          <div className="sp-field">
            <label className="sp-label">Feature list</label>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {form.items.map((it, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <input type="checkbox" checked={it.yes} title="Included in this plan"
                    onChange={(e) => updateItem(i, { yes: e.target.checked })} />
                  <input className="sp-input" style={{ flex: 1 }} placeholder="e.g. Priority customer support"
                    value={it.label} onChange={(e) => updateItem(i, { label: e.target.value })} />
                  <button className="sp-btn sp-btn-ghost sp-btn-icon sp-btn-sm" title="Remove"
                    onClick={() => removeItem(i)}>
                    <X size={12} />
                  </button>
                </div>
              ))}
              <button className="sp-btn sp-btn-ghost sp-btn-sm" style={{ width: "fit-content" }} onClick={addItem}>
                <Plus size={12} /> Add feature
              </button>
            </div>
            <span className="sp-input-hint">Check the box for features included in this plan; leave unchecked for greyed-out items shown as not included</span>
          </div>
        </div>

        <div className="sp-modal-footer">
          <button className="sp-btn sp-btn-ghost" onClick={onClose}>Cancel</button>
          <button className="sp-btn sp-btn-primary" onClick={save} disabled={saving}>
            {saving ? "Saving…" : isEdit ? "Save changes" : "Create plan"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Confirm delete dialog ────────────────────────────────────────────────────
function ConfirmDeletePlan({ plan, onCancel, onConfirm, busy }) {
  return (
    <div className="sp-confirm-bg">
      <div className="sp-confirm">
        <div className="sp-confirm-icon" style={{ background: "var(--rose-dim)", border: "1px solid rgba(251,113,133,.2)" }}>
          <Trash2 size={18} style={{ color: "var(--rose)" }} />
        </div>
        <h3>Delete "{plan.name}" plan?</h3>
        <p>
          This will permanently remove this plan. Existing subscribers will keep access until their current period ends,
          but won't be able to renew or choose this plan.
        </p>
        <div className="sp-confirm-actions">
          <button className="sp-btn sp-btn-ghost" onClick={onCancel} disabled={busy}>Cancel</button>
          <button className="sp-btn sp-btn-danger" onClick={onConfirm} disabled={busy}>
            {busy ? "Deleting…" : "Delete plan"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Add subscription modal (assign a plan to a user) ────────────────────────
function AddSubModal({ plans, onClose, onSaved, addToast }) {
  const [users,        setUsers]        = useState([]);
  const [usersLoading, setUsersLoading]  = useState(true);
  const [userFilter,   setUserFilter]   = useState("");

  const [userId,   setUserId]   = useState("");
  const [planId,   setPlanId]   = useState(plans[0]?.id ?? "");
  const [cycle,    setCycle]    = useState("monthly");
  const [start,    setStart]    = useState(() => new Date().toISOString().slice(0, 10));
  const [status,   setStatus]   = useState("active");
  const [saving,   setSaving]   = useState(false);

  useEffect(() => {
    (async () => {
      setUsersLoading(true);
      const { data, error } = await supabase
        .from("users")
        .select("id,first_name,last_name,email,plan,status")
        .order("first_name", { ascending: true });
      if (error) addToast(error.message, "error");
      setUsers(data ?? []);
      setUsersLoading(false);
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredUsers = userFilter.trim()
    ? users.filter((u) => {
        const q = userFilter.toLowerCase();
        return `${u.first_name} ${u.last_name}`.toLowerCase().includes(q)
          || (u.email ?? "").toLowerCase().includes(q);
      })
    : users;

  const selUser = users.find((u) => u.id === userId) ?? null;
  const selPlan = plans.find((p) => String(p.id) === String(planId)) ?? null;

  function computeEnd(startDate, billingCycle) {
    const d = new Date(startDate);
    if (billingCycle === "annual") d.setFullYear(d.getFullYear() + 1);
    else d.setMonth(d.getMonth() + 1);
    return d.toISOString();
  }

  async function save() {
    if (!userId) { addToast("Select a user", "error"); return; }
    if (!planId) { addToast("Select a plan", "error"); return; }
    setSaving(true);

    const payload = {
      user_id: userId,
      plan_id: planId,
      billing_cycle: cycle,
      status,
      current_period_start: new Date(start).toISOString(),
      current_period_end: computeEnd(start, cycle),
    };
    const { error: subError } = await supabase.from("user_subscriptions").insert(payload);
    if (subError) { setSaving(false); addToast(subError.message, "error"); return; }

    // keep users.plan in sync with the assigned subscription plan
    if (selPlan) {
      const { error: userError } = await supabase
        .from("users")
        .update({ plan: selPlan.name.toLowerCase() })
        .eq("id", userId);
      if (userError) addToast(`Subscription added, but failed to sync user plan: ${userError.message}`, "error");
    }

    setSaving(false);
    addToast(`${selPlan?.name ?? "Plan"} subscription assigned to ${selUser?.first_name ?? "user"}`, "success");
    onSaved();
  }

  return (
    <div className="sp-modal-bg" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="sp-modal">
        <div className="sp-modal-header">
          <span className="sp-modal-title">Add subscription to user</span>
          <button className="sp-btn sp-btn-ghost sp-btn-icon" onClick={onClose}><X size={14} /></button>
        </div>
        <div className="sp-modal-body">

          {/* User dropdown */}
          <div className="sp-field">
            <label className="sp-label">User *</label>
            <input
              className="sp-input"
              placeholder="Filter by name or email…"
              value={userFilter}
              onChange={(e) => setUserFilter(e.target.value)}
              style={{ marginBottom: 8 }}
            />
            <select
              className="sp-input"
              style={{ appearance: "auto" }}
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              disabled={usersLoading}
            >
              <option value="">{usersLoading ? "Loading users…" : "Select a user…"}</option>
              {filteredUsers.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.first_name} {u.last_name} — {u.email} (current plan: {u.plan})
                </option>
              ))}
            </select>

            {selUser && (
              <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 13px", marginTop: 8, background: "var(--bg-elevated)", border: "1px solid var(--border-hi)", borderRadius: "var(--radius-sm)" }}>
                <div className="sp-avatar" style={{ width: 28, height: 28, fontSize: 10 }}>
                  {initials(selUser.first_name, selUser.last_name)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 600, color: "var(--text-primary)" }}>
                    {selUser.first_name} {selUser.last_name}
                  </div>
                  <div style={{ fontSize: 10.5, color: "var(--text-muted)" }}>{selUser.email}</div>
                </div>
                <span className={`sp-chip ${selUser.status === "active" ? "active" : "cancelled"}`} style={{ flexShrink: 0 }}>
                  {selUser.status}
                </span>
              </div>
            )}
          </div>

          {/* Plan + cycle */}
          <div className="sp-field-row">
            <div className="sp-field">
              <label className="sp-label">Plan *</label>
              <select className="sp-input" style={{ appearance: "auto" }} value={planId} onChange={(e) => setPlanId(e.target.value)}>
                {plans.map((p) => <option key={p.id} value={p.id}>{p.name} — {fmt$(p.monthly_price)}/mo</option>)}
              </select>
            </div>
            <div className="sp-field">
              <label className="sp-label">Billing cycle</label>
              <select className="sp-input" style={{ appearance: "auto" }} value={cycle} onChange={(e) => setCycle(e.target.value)}>
                <option value="monthly">Monthly</option>
                <option value="annual">Annual</option>
              </select>
            </div>
          </div>

          {/* Start date + status */}
          <div className="sp-field-row">
            <div className="sp-field">
              <label className="sp-label">Period start</label>
              <input className="sp-input" type="date" value={start} onChange={(e) => setStart(e.target.value)} />
              <span className="sp-input-hint">Period end auto-calculated from billing cycle</span>
            </div>
            <div className="sp-field">
              <label className="sp-label">Subscription status</label>
              <select className="sp-input" style={{ appearance: "auto" }} value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="active">Active</option>
                <option value="paused">Paused</option>
              </select>
            </div>
          </div>

          <div className="sp-input-hint" style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <AlertCircle size={11} /> This also updates the user's <code style={{ fontFamily: "var(--font-mono)" }}>plan</code> field to match.
          </div>

        </div>

        <div className="sp-modal-footer">
          <button className="sp-btn sp-btn-ghost" onClick={onClose}>Cancel</button>
          <button className="sp-btn sp-btn-primary" onClick={save} disabled={saving || !userId || !planId}>
            {saving ? "Assigning…" : "Assign subscription"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Subscriber detail drawer ─────────────────────────────────────────────────
function SubDrawer({ sub, onClose, onAction, actionBusy }) {
  const theme = getPlanTheme(sub.plan?.name);
  const PlanIcon = theme.icon;
  const days = daysLeft(sub.current_period_end);

  return (
    <>
      <div className="sp-drawer-bg" onClick={onClose} />
      <div className="sp-drawer">
        <div className="sp-drawer-header">
          <div className="sp-drawer-title">Subscription detail</div>
          <button className="sp-btn sp-btn-ghost sp-btn-icon" onClick={onClose}><X size={14} /></button>
        </div>

        <div className="sp-drawer-body">
          {/* User */}
          <div className="sp-drawer-section">
            <div className="sp-drawer-section-title">Subscriber</div>
            <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", background: "var(--bg-card)", borderRadius: "var(--radius-sm)", border: "1px solid var(--border)" }}>
              <div className="sp-avatar" style={{ width: 38, height: 38, fontSize: 13 }}>
                {initials(sub.user?.first_name, sub.user?.last_name)}
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-primary)" }}>
                  {sub.user?.first_name ?? "—"} {sub.user?.last_name ?? ""}
                </div>
                <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>{sub.user?.email ?? "—"}</div>
              </div>
            </div>
          </div>

          {/* Plan */}
          <div className="sp-drawer-section">
            <div className="sp-drawer-section-title">Plan</div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", background: "var(--bg-card)", borderRadius: "var(--radius-sm)", border: "1px solid var(--border)" }}>
              <div style={{ width: 34, height: 34, borderRadius: 10, background: theme.iconBg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <PlanIcon size={15} style={{ color: theme.iconColor }} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text-primary)" }}>{sub.plan?.name ?? "Unknown"}</div>
                <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>
                  <span className={`sp-chip ${sub.billing_cycle}`} style={{ fontSize: 9 }}>{sub.billing_cycle}</span>
                </div>
              </div>
              <span className={`sp-chip ${sub.status}`} style={{ flexShrink: 0 }}>
                {sub.status === "active" && <span className="sp-dot-pulse" />}
                {sub.status}
              </span>
            </div>
          </div>

          {/* Details */}
          <div className="sp-drawer-section">
            <div className="sp-drawer-section-title">Details</div>
            {[
              { label: "Status",        val: sub.status },
              { label: "Billing",       val: sub.billing_cycle },
              { label: "Period start",  val: fmtDate(sub.current_period_start) },
              { label: "Period end",    val: fmtDate(sub.current_period_end) },
              { label: "Days remaining",val: days !== null ? (days > 0 ? `${days} days` : "Expired") : "—", danger: days !== null && days <= 7 && days >= 0 },
              { label: "Subscribed",    val: fmtDate(sub.created_at) },
              { label: "Cancelled at",  val: fmtDate(sub.cancelled_at), danger: !!sub.cancelled_at },
            ].map(({ label, val, danger }) => (
              <div key={label} className="sp-drawer-row">
                <span className="sp-drawer-row-label">{label}</span>
                <span className="sp-drawer-row-val" style={{ color: danger ? "var(--rose)" : undefined }}>{val}</span>
              </div>
            ))}
          </div>

          {/* ID */}
          <div className="sp-drawer-section">
            <div className="sp-drawer-section-title">Internal</div>
            <div className="sp-drawer-row" style={{ display: "block" }}>
              <div className="sp-drawer-row-label" style={{ marginBottom: 4 }}>Subscription ID</div>
              <div style={{ fontSize: 10.5, color: "var(--text-muted)", fontFamily: "var(--font-mono)", wordBreak: "break-all" }}>{sub.id}</div>
            </div>
            <div className="sp-drawer-row" style={{ display: "block" }}>
              <div className="sp-drawer-row-label" style={{ marginBottom: 4 }}>User ID</div>
              <div style={{ fontSize: 10.5, color: "var(--text-muted)", fontFamily: "var(--font-mono)", wordBreak: "break-all" }}>{sub.user_id}</div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="sp-drawer-actions" style={{ borderTop: "1px solid var(--border)", paddingTop: 16 }}>
          {sub.status === "active" && (
            <button className="sp-btn sp-btn-danger" style={{ width: "100%", justifyContent: "center" }}
              disabled={actionBusy} onClick={() => onAction("cancel", sub)}>
              <Ban size={13} /> {actionBusy ? "Cancelling…" : "Cancel subscription"}
            </button>
          )}
          {(sub.status === "cancelled" || sub.status === "expired") && (
            <button className="sp-btn sp-btn-success" style={{ width: "100%", justifyContent: "center" }}
              disabled={actionBusy} onClick={() => onAction("reactivate", sub)}>
              <RotateCcw size={13} /> {actionBusy ? "Reactivating…" : "Reactivate subscription"}
            </button>
          )}
          <button className="sp-btn sp-btn-ghost" style={{ width: "100%", justifyContent: "center" }} onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </>
  );
}

// ─── Main page component ──────────────────────────────────────────────────────
export default function AdminSubscriptions() {
  const navigate = useNavigate();

  // auth
  const [session,        setSession]        = useState(null);
  const [sesLoading,     setSesLoading]     = useState(true);

  // mobile sidebar
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const closeSidebar = () => setSidebarOpen(false);
  useEffect(() => {
    document.body.style.overflow = sidebarOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [sidebarOpen]);

  // plans
  const [plans,        setPlans]        = useState([]);
  const [planSubs,     setPlanSubs]     = useState({});
  const [plansLoading, setPlansLoading] = useState(true);
  const [planModal,    setPlanModal]    = useState(null); // null | {} | planObj
  const [deletePlan,   setDeletePlan]   = useState(null);
  const [deleteBusy,   setDeleteBusy]   = useState(false);

  // subscribers
  const [subs,         setSubs]         = useState([]);
  const [subsLoading,  setSubsLoading]  = useState(true);
  const [subsTotal,    setSubsTotal]    = useState(0);
  const [page,         setPage]         = useState(1);
  const [search,       setSearch]       = useState("");
  const [fStatus,      setFStatus]      = useState("all");
  const [fPlan,        setFPlan]        = useState("all");
  const [fCycle,       setFCycle]       = useState("all");

  // stats
  const [stats, setStats] = useState({ total: 0, active: 0, monthly: 0, annual: 0, cancelled: 0 });

  // drawer
  const [drawerSub,  setDrawerSub]  = useState(null);
  const [actionBusy, setActionBusy] = useState(false);

  // add subscription
  const [addSubOpen, setAddSubOpen] = useState(false);

  // page tabs: "plans" | "subscribers"
  const [activeTab, setActiveTab] = useState("plans");

  // toasts
  const [toasts, setToasts] = useState([]);
  const addToast = useCallback((msg, type = "success") => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, { id, msg, type }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 3800);
  }, []);

  // ── Auth ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session: s } }) => {
      if (!s) { navigate("/admin/login", { replace: true }); return; }
      setSession(s); setSesLoading(false); loadAll();
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, s) => {
      if (!s) navigate("/admin/login", { replace: true });
      else setSession(s);
    });
    return () => subscription.unsubscribe();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadAll() {
    await Promise.all([fetchPlans(), fetchStats()]);
    fetchSubs();
  }

  // ── Plans ─────────────────────────────────────────────────────────────────
  async function fetchPlans() {
    setPlansLoading(true);
    const { data: planData } = await supabase
      .from("subscription_plans").select("*").order("monthly_price", { ascending: true });

    // subscriber counts per plan
    const { data: countRows } = await supabase
      .from("user_subscriptions").select("plan_id");
    const counts = {};
    (countRows ?? []).forEach(({ plan_id }) => { counts[plan_id] = (counts[plan_id] ?? 0) + 1; });

    setPlans(planData ?? []);
    setPlanSubs(counts);
    setPlansLoading(false);
  }

  // ── Stats ─────────────────────────────────────────────────────────────────
  async function fetchStats() {
    const [
      { count: total     },
      { count: active    },
      { count: monthly   },
      { count: annual    },
      { count: cancelled },
    ] = await Promise.all([
      supabase.from("user_subscriptions").select("*", { count: "exact", head: true }),
      supabase.from("user_subscriptions").select("*", { count: "exact", head: true }).eq("status", "active"),
      supabase.from("user_subscriptions").select("*", { count: "exact", head: true }).eq("billing_cycle", "monthly"),
      supabase.from("user_subscriptions").select("*", { count: "exact", head: true }).eq("billing_cycle", "annual"),
      supabase.from("user_subscriptions").select("*", { count: "exact", head: true }).eq("status", "cancelled"),
    ]);
    setStats({
      total:     total     ?? 0,
      active:    active    ?? 0,
      monthly:   monthly   ?? 0,
      annual:    annual    ?? 0,
      cancelled: cancelled ?? 0,
    });
  }

  // ── Subscribers (paginated) ───────────────────────────────────────────────
  async function fetchSubs(
    pg      = page,
    sch     = search,
    status  = fStatus,
    planId  = fPlan,
    cycle   = fCycle,
  ) {
    setSubsLoading(true);
    const from = (pg - 1) * PAGE_SIZE;
    const to   = from + PAGE_SIZE - 1;

    let q = supabase
      .from("user_subscriptions")
      .select("id,billing_cycle,status,current_period_start,current_period_end,cancelled_at,created_at,plan_id,user_id", { count: "exact" })
      .order("created_at", { ascending: false })
      .range(from, to);

    if (status !== "all") q = q.eq("status", status);
    if (cycle  !== "all") q = q.eq("billing_cycle", cycle);
    if (planId !== "all") q = q.eq("plan_id", planId);

    const { data: raw, count, error } = await q;
    if (error) { setSubsLoading(false); addToast(error.message, "error"); return; }

    // client-side merge
    const userIds = [...new Set((raw ?? []).map((r) => r.user_id))];
    const planIds = [...new Set((raw ?? []).map((r) => r.plan_id))];

    const [{ data: users }, { data: planNames }] = await Promise.all([
      userIds.length
        ? supabase.from("users").select("id,first_name,last_name,email").in("id", userIds)
        : Promise.resolve({ data: [] }),
      planIds.length
        ? supabase.from("subscription_plans").select("id,name").in("id", planIds)
        : Promise.resolve({ data: [] }),
    ]);

    const userMap = Object.fromEntries((users ?? []).map((u) => [u.id, u]));
    const planMap = Object.fromEntries((planNames ?? []).map((p) => [p.id, p]));

    let merged = (raw ?? []).map((s) => ({
      ...s, user: userMap[s.user_id] ?? null, plan: planMap[s.plan_id] ?? null,
    }));

    // client-side search
    if (sch.trim()) {
      const q2 = sch.toLowerCase();
      merged = merged.filter((s) => {
        const name  = `${s.user?.first_name ?? ""} ${s.user?.last_name ?? ""}`.toLowerCase();
        const email = (s.user?.email ?? "").toLowerCase();
        const plan  = (s.plan?.name ?? "").toLowerCase();
        return name.includes(q2) || email.includes(q2) || plan.includes(q2);
      });
    }

    setSubs(merged);
    setSubsTotal(count ?? 0);
    setSubsLoading(false);
  }

  // Re-fetch on filter/page change
  const prevFilters = useRef({ page, fStatus, fPlan, fCycle });
  useEffect(() => {
    if (sesLoading) return;
    const prev = prevFilters.current;
    if (prev.page !== page || prev.fStatus !== fStatus || prev.fPlan !== fPlan || prev.fCycle !== fCycle) {
      prevFilters.current = { page, fStatus, fPlan, fCycle };
      fetchSubs(page, search, fStatus, fPlan, fCycle);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, fStatus, fPlan, fCycle]);

  function applySearch(v) {
    setSearch(v); setPage(1);
    fetchSubs(1, v, fStatus, fPlan, fCycle);
  }
  function applyFilter(key, val) {
    setPage(1);
    if (key === "status") { setFStatus(val); fetchSubs(1, search, val, fPlan, fCycle); }
    if (key === "plan")   { setFPlan(val);   fetchSubs(1, search, fStatus, val, fCycle); }
    if (key === "cycle")  { setFCycle(val);  fetchSubs(1, search, fStatus, fPlan, val); }
  }

  // ── Delete plan ───────────────────────────────────────────────────────────
  async function doDeletePlan() {
    setDeleteBusy(true);
    const { error } = await supabase.from("subscription_plans").delete().eq("id", deletePlan.id);
    setDeleteBusy(false);
    if (error) { addToast(error.message, "error"); return; }
    setDeletePlan(null);
    addToast(`"${deletePlan.name}" plan deleted`, "success");
    fetchPlans(); fetchStats();
  }

  // ── Subscriber actions ────────────────────────────────────────────────────
  async function handleSubAction(action, sub) {
    setActionBusy(true);
    if (action === "cancel") {
      const { error } = await supabase.from("user_subscriptions")
        .update({ status: "cancelled", cancelled_at: new Date().toISOString() })
        .eq("id", sub.id);
      if (error) { addToast(error.message, "error"); }
      else {
        addToast("Subscription cancelled", "success");
        setDrawerSub(null);
        fetchStats(); fetchSubs();
      }
    }
    if (action === "reactivate") {
      const { error } = await supabase.from("user_subscriptions")
        .update({ status: "active", cancelled_at: null })
        .eq("id", sub.id);
      if (error) { addToast(error.message, "error"); }
      else {
        addToast("Subscription reactivated", "success");
        setDrawerSub(null);
        fetchStats(); fetchSubs();
      }
    }
    setActionBusy(false);
  }

  async function handleLogout() { await supabase.auth.signOut(); }

  const adminEmail    = session?.user?.email ?? "admin@vantrexmarket.io";
  const adminInitials = adminEmail.slice(0, 2).toUpperCase();
  const totalPages    = Math.ceil(subsTotal / PAGE_SIZE);

  // ── Loading gate ──────────────────────────────────────────────────────────
  if (sesLoading) return (
    <>
      <StyleInjector />
      <div style={{ height: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "var(--bg-base)", flexDirection: "column", gap: 14 }}>
        <div style={{ width: 38, height: 38, borderRadius: 11, background: "linear-gradient(135deg,#6366f1,#a78bfa)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Shield size={17} color="#fff" />
        </div>
        <div style={{ fontSize: 12, color: "var(--text-muted)" }}>Verifying session…</div>
      </div>
    </>
  );

  // ── KPI data ──────────────────────────────────────────────────────────────
  const kpis = [
    { label: "Total subscriptions", val: stats.total,     icon: CreditCard,  iconBg: "var(--accent-dim)",  iconColor: "var(--accent-light)", bar: "linear-gradient(to right,#6366f1,#8b5cf6)", sub: `${stats.active} active`, subCls: "up" },
    { label: "Active",              val: stats.active,    icon: BadgeCheck,   iconBg: "var(--green-dim)",   iconColor: "var(--green)",        bar: "linear-gradient(to right,#34d399,#059669)", sub: stats.total ? `${Math.round(stats.active/stats.total*100)}% retention` : "—", subCls: "up" },
    { label: "Monthly billing",     val: stats.monthly,   icon: Calendar,     iconBg: "var(--blue-dim)",    iconColor: "var(--blue)",          bar: "linear-gradient(to right,#60a5fa,#3b82f6)", sub: "billing monthly", subCls: "neu" },
    { label: "Annual billing",      val: stats.annual,    icon: TrendingUp,   iconBg: "var(--cyan-dim)",    iconColor: "var(--cyan)",          bar: "linear-gradient(to right,#22d3ee,#0891b2)", sub: "billing annually", subCls: "up" },
    { label: "Cancelled",           val: stats.cancelled, icon: XCircle,      iconBg: "var(--rose-dim)",    iconColor: "var(--rose)",          bar: "linear-gradient(to right,#fb7185,#e11d48)", sub: stats.total ? `${Math.round(stats.cancelled/stats.total*100)}% churn` : "—", subCls: "down" },
  ];

  return (
    <>
      <StyleInjector />
      <Toasts list={toasts} />

      {planModal !== null && (
        <PlanModal
          plan={planModal?.id ? planModal : null}
          onClose={() => setPlanModal(null)}
          onSaved={() => { setPlanModal(null); fetchPlans(); fetchStats(); }}
          addToast={addToast}
        />
      )}
      {deletePlan && (
        <ConfirmDeletePlan
          plan={deletePlan}
          onCancel={() => setDeletePlan(null)}
          onConfirm={doDeletePlan}
          busy={deleteBusy}
        />
      )}
      {drawerSub && (
        <SubDrawer
          sub={drawerSub}
          onClose={() => setDrawerSub(null)}
          onAction={handleSubAction}
          actionBusy={actionBusy}
        />
      )}
      {addSubOpen && (
        <AddSubModal
          plans={plans}
          onClose={() => setAddSubOpen(false)}
          onSaved={() => { setAddSubOpen(false); fetchStats(); fetchSubs(); fetchPlans(); }}
          addToast={addToast}
        />
      )}

      <div className="ad-shell">
        {/* Overlay */}
        <div className={`ad-overlay${sidebarOpen ? " open" : ""}`} onClick={closeSidebar} aria-hidden="true" />

        {/* Sidebar */}
        <aside className={`ad-sidebar${sidebarOpen ? " open" : ""}`}>
          <div className="ad-logo">
            <div className="ad-logo-icon"><Shield size={13} color="#fff" /></div>
            <div className="ad-logo-text">
              <strong>vantrexmarket</strong>
              <span>Admin panel</span>
            </div>
            <button className="ad-logo-close" onClick={closeSidebar}><X size={14} /></button>
          </div>
          <nav className="ad-nav">
            {NAV_SECTIONS.map((sec) => (
              <div key={sec.label}>
                <p className="ad-nav-section-label">{sec.label}</p>
                <div className="ad-nav-items">
                  {sec.items.map((item) => (
                    <SidebarLink key={item.to} {...item} onClick={closeSidebar} />
                  ))}
                </div>
              </div>
            ))}
          </nav>
          <div className="ad-user-card">
            <div className="ad-user-inner">
              <div className="ad-user-avatar">{adminInitials}</div>
              <div className="ad-user-info">
                <strong>Super Admin</strong>
                <span>{adminEmail}</span>
              </div>
              <button className="ad-logout-btn" onClick={handleLogout} title="Logout">
                <LogOut size={13} />
              </button>
            </div>
          </div>
        </aside>

        {/* Page */}
        <div className="ad-page">

          {/* Topbar */}
          <header className="ad-topbar">
            <div className="ad-topbar-left">
              <button className="ad-hamburger" onClick={() => setSidebarOpen(true)} aria-label="Open menu">
                <Menu size={16} />
              </button>
              <div>
                <div className="ad-topbar-title">Subscriptions</div>
                <div className="ad-topbar-sub">Manage plans &amp; subscribers</div>
              </div>
            </div>
            <div className="ad-topbar-right">
              <button className="sp-btn sp-btn-ghost sp-btn-sm" onClick={loadAll} title="Refresh">
                <RefreshCw size={12} /> <span style={{ fontSize: 11 }}>Refresh</span>
              </button>
              <button className="sp-btn sp-btn-success sp-btn-sm" onClick={() => setAddSubOpen(true)}>
                <UserCheck size={13} /> Add subscription
              </button>
              <button className="sp-btn sp-btn-primary sp-btn-sm" onClick={() => setPlanModal({})}>
                <Plus size={13} /> New plan
              </button>
            </div>
          </header>

          {/* Main */}
          <main className="ad-main">

            {/* Page heading */}
            <div className="sp-page-header">
              <div className="sp-page-header-text">
                <h1>Subscription Management</h1>
                <p>Create and manage subscription plans, monitor active subscribers, cancel or reactivate subscriptions.</p>
              </div>
            </div>

            {/* Tabs */}
            <div className="sp-tabs">
              <button className={`sp-tab${activeTab === "plans" ? " active" : ""}`} onClick={() => setActiveTab("plans")}>
                <CreditCard size={13} /> Plans
                <span className="sp-tab-count">{plans.length}</span>
              </button>
              <button className={`sp-tab${activeTab === "subscribers" ? " active" : ""}`} onClick={() => setActiveTab("subscribers")}>
                <Users size={13} /> User Subscriptions
                <span className="sp-tab-count">{subsTotal}</span>
              </button>
            </div>

            {/* KPI row */}
            <div className="sp-kpi-row">
              {kpis.map(({ label, val, icon: Icon, iconBg, iconColor, bar, sub, subCls }) => (
                <div key={label} className="sp-kpi" style={{ "--kpi-bar": bar }}>
                  <div className="sp-kpi-icon" style={{ background: iconBg }}>
                    <Icon size={17} style={{ color: iconColor }} />
                  </div>
                  <div className="sp-kpi-body">
                    <div className="sp-kpi-val">{val.toLocaleString()}</div>
                    <div className="sp-kpi-label">{label}</div>
                    <div className={`sp-kpi-sub ${subCls}`}>
                      {subCls === "up"   && <TrendingUp   size={10} />}
                      {subCls === "down" && <TrendingDown  size={10} />}
                      {sub}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* ── Plans ── */}
            {activeTab === "plans" && (
            <div>
              <div className="sp-section-label">
                <CreditCard size={12} />
                Subscription plans
                <span className="sp-section-count">{plans.length} plan{plans.length !== 1 ? "s" : ""}</span>
              </div>

              {plansLoading ? (
                <div className="sp-plans-grid">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="sp-plan-card" style={{ gap: 12 }}>
                      {[60, 40, 55, 90, 75, 50, 80].map((w, j) => (
                        <div key={j} className="sp-skel" style={{ width: `${w}%`, height: j === 2 ? 28 : 10 }} />
                      ))}
                    </div>
                  ))}
                </div>
              ) : plans.length === 0 ? (
                <div className="sp-panel">
                  <div className="sp-empty">
                    <CreditCard size={32} />
                    <div style={{ fontWeight: 600, color: "var(--text-secondary)" }}>No plans yet</div>
                    <div>Create your first subscription plan to get started.</div>
                    <button className="sp-btn sp-btn-primary" style={{ marginTop: 8 }} onClick={() => setPlanModal({})}>
                      <Plus size={13} /> Create first plan
                    </button>
                  </div>
                </div>
              ) : (
                <div className="sp-plans-grid">
                  {plans.map((p) => (
                    <PlanCard
                      key={p.id}
                      plan={p}
                      subCount={planSubs[p.id] ?? 0}
                      onEdit={(pl) => setPlanModal(pl)}
                      onDelete={(pl) => setDeletePlan(pl)}
                    />
                  ))}
                </div>
              )}
            </div>
            )}

            {/* ── Subscribers table ── */}
            {activeTab === "subscribers" && (
            <div className="sp-panel">
              <div className="sp-panel-header">
                <div>
                  <div className="sp-panel-title">Subscribers</div>
                  <div className="sp-panel-sub">
                    {subsTotal.toLocaleString()} total subscription{subsTotal !== 1 ? "s" : ""}
                    {fStatus !== "all" || fPlan !== "all" || fCycle !== "all" || search
                      ? " (filtered)" : ""}
                  </div>
                </div>
                <div className="sp-toolbar">
                  <div className="sp-search-wrap">
                    <Search size={12} />
                    <input
                      className="sp-search"
                      placeholder="Search name, email, plan…"
                      value={search}
                      onChange={(e) => applySearch(e.target.value)}
                    />
                  </div>
                  <select className="sp-select" value={fStatus} onChange={(e) => applyFilter("status", e.target.value)}>
                    <option value="all">All statuses</option>
                    <option value="active">Active</option>
                    <option value="cancelled">Cancelled</option>
                    <option value="expired">Expired</option>
                    <option value="paused">Paused</option>
                  </select>
                  <select className="sp-select" value={fPlan} onChange={(e) => applyFilter("plan", e.target.value)}>
                    <option value="all">All plans</option>
                    {plans.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                  <select className="sp-select" value={fCycle} onChange={(e) => applyFilter("cycle", e.target.value)}>
                    <option value="all">All cycles</option>
                    <option value="monthly">Monthly</option>
                    <option value="annual">Annual</option>
                  </select>
                </div>
              </div>

              <div className="sp-table-wrap">
                <table className="sp-table">
                  <thead>
                    <tr>
                      <th>Subscriber</th>
                      <th>Plan</th>
                      <th>Billing</th>
                      <th>Status</th>
                      <th>Period</th>
                      <th>Days left</th>
                      <th>Subscribed</th>
                      <th>Cancelled</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subsLoading
                      ? Array.from({ length: 8 }).map((_, i) => (
                          <tr key={i}>
                            {Array.from({ length: 8 }).map((_, j) => (
                              <td key={j} style={{ padding: "13px 16px" }}>
                                <div className="sp-skel" style={{ width: `${35 + (j * 17) % 50}%` }} />
                              </td>
                            ))}
                          </tr>
                        ))
                      : subs.length === 0
                      ? (
                          <tr>
                            <td colSpan={8}>
                              <div className="sp-empty">
                                <Users size={24} />
                                <div style={{ fontWeight: 600 }}>No subscribers found</div>
                                <div>Try adjusting your filters or search query.</div>
                              </div>
                            </td>
                          </tr>
                        )
                      : subs.map((s) => {
                          const chipCls  = ["active","cancelled","expired","paused"].includes(s.status) ? s.status : "active";
                          const theme    = getPlanTheme(s.plan?.name);
                          const PlanIcon = theme.icon;
                          const days     = daysLeft(s.current_period_end);
                          return (
                            <tr key={s.id} onClick={() => setDrawerSub(s)} title="Click to view details">
                              {/* Subscriber */}
                              <td>
                                <div className="sp-avatar-cell">
                                  <div className="sp-avatar">{initials(s.user?.first_name, s.user?.last_name)}</div>
                                  <div>
                                    <div className="sp-cell-name">{s.user?.first_name ?? "—"} {s.user?.last_name ?? ""}</div>
                                    <div className="sp-cell-email">{s.user?.email ?? "—"}</div>
                                  </div>
                                </div>
                              </td>
                              {/* Plan */}
                              <td>
                                <span className="sp-plan-pill">
                                  <PlanIcon size={9} /> {s.plan?.name ?? "—"}
                                </span>
                              </td>
                              {/* Billing */}
                              <td><span className={`sp-chip ${s.billing_cycle}`}>{s.billing_cycle}</span></td>
                              {/* Status */}
                              <td>
                                <span className={`sp-chip ${chipCls}`}>
                                  {s.status === "active" && <span className="sp-dot-pulse" />}
                                  {s.status}
                                </span>
                              </td>
                              {/* Period */}
                              <td>
                                <span className="sp-cell-mono">
                                  {fmtDateShort(s.current_period_start)}&nbsp;→&nbsp;{fmtDateShort(s.current_period_end)}
                                </span>
                              </td>
                              {/* Days left */}
                              <td>
                                <span className="sp-cell-mono" style={{
                                  color: days === null ? "var(--text-muted)"
                                    : days <= 0  ? "var(--rose)"
                                    : days <= 7  ? "var(--amber)"
                                    : "var(--text-secondary)"
                                }}>
                                  {days === null ? "—" : days <= 0 ? "Expired" : `${days}d`}
                                </span>
                              </td>
                              {/* Subscribed */}
                              <td><span className="sp-cell-date">{fmtDate(s.created_at)}</span></td>
                              {/* Cancelled */}
                              <td>
                                <span className="sp-cell-date" style={{ color: s.cancelled_at ? "var(--rose)" : "var(--text-muted)" }}>
                                  {fmtDate(s.cancelled_at)}
                                </span>
                              </td>
                            </tr>
                          );
                        })
                    }
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {!subsLoading && totalPages > 1 && (
                <div className="sp-pagination">
                  <span className="sp-pagination-info">
                    Showing {Math.min((page - 1) * PAGE_SIZE + 1, subsTotal)}–{Math.min(page * PAGE_SIZE, subsTotal)} of {subsTotal.toLocaleString()}
                  </span>
                  <div className="sp-pagination-btns">
                    <button className="sp-page-btn" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
                      <ChevronLeft size={13} />
                    </button>
                    {(() => {
                      const pages = [];
                      const total = Math.min(totalPages, 7);
                      let start = Math.max(1, page - 3);
                      let end   = Math.min(totalPages, start + total - 1);
                      if (end - start < total - 1) start = Math.max(1, end - total + 1);
                      for (let p = start; p <= end; p++) {
                        pages.push(
                          <button key={p} className={`sp-page-btn${page === p ? " active" : ""}`} onClick={() => setPage(p)}>
                            {p}
                          </button>
                        );
                      }
                      return pages;
                    })()}
                    <button className="sp-page-btn" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
                      <ChevronRight size={13} />
                    </button>
                  </div>
                </div>
              )}
            </div>
            )}

          </main>
        </div>
      </div>
    </>
  );
}